import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthProvider';
import { teamManagerService } from '../services/teamManagerService';

/**
 * Auth gate for team-manager routes.
 * - Redirects unauthenticated users to /team/login.
 * - For authenticated users, checks team_managers (RLS-scoped to own row).
 *   If no row, shows a friendly "no access" screen with sign-out + retry.
 */
const TeamManagerRoute = ({ children }) => {
    const { user, loading: authLoading, logout } = useAuth();
    const location = useLocation();

    const [checking, setChecking] = useState(true);
    const [team, setTeam] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        if (authLoading || !user) {
            setChecking(false);
            return;
        }

        const verify = async () => {
            setChecking(true);
            setError(null);
            try {
                const tm = await teamManagerService.getMyTeam();
                if (!cancelled) setTeam(tm);
            } catch (err) {
                if (!cancelled) setError(err.message || 'Failed to verify access');
                console.error(err);
            } finally {
                if (!cancelled) setChecking(false);
            }
        };
        verify();

        return () => { cancelled = true; };
    }, [authLoading, user]);

    if (authLoading || checking) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/team/login" state={{ from: location }} replace />;
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
                <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
                    <h1 className="text-xl font-bold text-gray-900 mb-2">Couldn't verify access</h1>
                    <p className="text-sm text-gray-600 mb-4">{error}</p>
                    <button onClick={() => window.location.reload()} className="text-indigo-600 hover:text-indigo-700 text-sm font-medium">
                        Try again
                    </button>
                </div>
            </div>
        );
    }

    if (!team) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
                <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center space-y-4">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-100">
                        <svg className="w-7 h-7 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                    </div>
                    <h1 className="text-xl font-bold text-gray-900">No team manager access</h1>
                    <p className="text-sm text-gray-600">
                        The email <span className="font-medium">{user.email}</span> isn't registered as a team manager.
                        Contact your administrator if this is unexpected.
                    </p>
                    <button
                        onClick={logout}
                        className="text-indigo-600 hover:text-indigo-700 text-sm font-medium"
                    >
                        Sign out
                    </button>
                </div>
            </div>
        );
    }

    // Pass the verified team manager row to the child via context-like prop pattern.
    return React.cloneElement(children, { team });
};

export default TeamManagerRoute;
